import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

import {
  useAddTicketRiskComment,
  useDeleteTicketRiskComment,
  useGetTicketRiskComments,
} from '~/hooks/apis/support/useTicketRiskComments';

import { formatShortDateTime } from './ticketRiskRows';

/** Mirrors svc-support's COMMENT_MAX_LENGTH — a short update for the other leaders, not a case file. */
const COMMENT_MAX_LENGTH = 2000;

/** Comment bodies keep the author's line breaks; a very long one scrolls inside its block. */
const COMMENT_BODY_STYLE = {
  mt: 0.5,
  whiteSpace: 'pre-line',
  overflowWrap: 'anywhere',
  maxHeight: 200,
  overflowY: 'auto',
} as const;

interface Props {
  ticketId: string;
  /** The signed-in leader's `id_member`, so their own comments get a Delete button. */
  currentUserId: string | null;
}

/**
 * The ticket's comment thread. Anyone with dashboard access can add to it; each leader can delete
 * their own comments (svc-support enforces that too). Loaded lazily — only the dialog shows it.
 */
export default function TicketCommentsPanel({ ticketId, currentUserId }: Props) {
  const { data: comments, isLoading, error: loadError } = useGetTicketRiskComments(ticketId);
  const add = useAddTicketRiskComment();
  const remove = useDeleteTicketRiskComment();
  const [draft, setDraft] = useState('');

  const isPending = add.isPending || remove.isPending;
  const error = add.error ?? remove.error;

  const submit = () => {
    const body = draft.trim();
    if (!body) {
      return;
    }
    add.mutate({ ticketId, body }, { onSuccess: () => setDraft('') });
  };

  return (
    <Stack spacing={2}>
      {isLoading ? (
        <CircularProgress size={20} />
      ) : loadError ? (
        <Alert severity="error">Could not load the comments for this ticket.</Alert>
      ) : !comments || comments.length === 0 ? (
        <Typography color="text.disabled">No comments yet</Typography>
      ) : (
        <Stack spacing={1.5}>
          {comments.map((comment) => {
            const isMine = currentUserId !== null && comment.authorId === currentUserId;

            return (
              <Box key={comment.id}>
                <Stack direction="row" spacing={1} alignItems="baseline" useFlexGap flexWrap="wrap">
                  <Typography variant="body2" fontWeight={600}>
                    {comment.authorName}
                    {isMine ? ' (you)' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatShortDateTime(comment.createdAt)}
                  </Typography>
                  {isMine && (
                    <Tooltip title="Delete your comment">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Delete comment"
                        disabled={isPending}
                        onClick={() => remove.mutate({ ticketId, commentId: comment.id })}
                        sx={{ ml: 'auto', p: 0.25 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant="body2" component="p" sx={COMMENT_BODY_STYLE}>
                  {comment.body}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      <TextField
        label="Add a comment"
        size="small"
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        helperText={`${draft.length} / ${COMMENT_MAX_LENGTH}`}
        slotProps={{ htmlInput: { maxLength: COMMENT_MAX_LENGTH }, formHelperText: { sx: { textAlign: 'right' } } }}
      />
      <Box>
        <Button variant="contained" size="small" disabled={isPending || draft.trim().length === 0} onClick={submit}>
          Add comment
        </Button>
      </Box>

      {error && (
        <Alert severity="error">{error instanceof Error ? error.message : 'Could not update the comments'}</Alert>
      )}
    </Stack>
  );
}
